// The pages (Ola's QA walk, 9 Oct; P5, D98): the rows sit in the page with no scroll box of their own,
// drawn pageSize at a time (10 unless the reader picks 25 or 50 under Rows per page), with Previous page
// and Next page under them and the places the page holds ("11 to 20 of 312"). Sorting and the filters
// run over the whole list, which is then paged, so a change of either, or of the page size, starts
// again from the first page. Selecting every page is the bulk bar's (bulk-bar.js).
//
// B15: the pager counts what the count line above the list counts, the rows isCounted() keeps
// (toolbar.js): on a card table the rows still to do, so a list of 500 with one row done reads "1 to 10
// of 499" beside "499 searches". A folded row is drawn among them and takes no place of its own.

export default function pagerPart({ PAGE_SIZES }) {
    return {
        pageCount() {
            return Math.max(1, Math.ceil(this.matchedToDoCount() / this.pageSize));
        },

        // The pager's buttons and range show only while the rows the filters keep fill more than one page.
        hasPages() {
            return this.matchedToDoCount() > this.pageSize;
        },

        // Rows per page shows while a choice would change the page: more rows than its smallest size.
        hasPageSizes() {
            return this.matchedToDoCount() > PAGE_SIZES[0];
        },

        pageSizeChoices() {
            return PAGE_SIZES;
        },

        isFirstPage() {
            return this.currentPage <= 1;
        },

        isLastPage() {
            return this.currentPage >= this.pageCount();
        },

        /**
         * The page each row of `matched` is drawn on, in its order. The rows the count line counts fill a
         * page pageSize at a time. A folded row (a card table's, with its Undo) is drawn on the page of the
         * counted row after it, so it never takes a place a row to do would fill, and it stays on the page
         * it was folded on, since that row takes the folded row's place in the count; past the last one, on
         * the last page. On any other table every row counts, and a page is pageSize rows.
         */
        pageOfEachRow() {
            const pages = [];
            let counted = 0;
            let waiting = [];

            this.matched.forEach((row, index) => {
                if (!this.isCounted(row)) {
                    waiting.push(index);

                    return;
                }

                const page = Math.floor(counted / this.pageSize) + 1;

                waiting.forEach((at) => {
                    pages[at] = page;
                });
                waiting = [];
                pages[index] = page;
                counted++;
            });

            const last = Math.max(1, Math.ceil(counted / this.pageSize));

            waiting.forEach((at) => {
                pages[at] = last;
            });

            return pages;
        },

        rowsOnPage(page) {
            const pages = this.pageOfEachRow();

            return this.matched.filter((row, index) => pages[index] === page);
        },

        // "11 to 20 of 312": places among the rows the count line counts, so its last figure is the line's.
        pageRange() {
            const count = this.matchedToDoCount();
            const from = count === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
            const to = Math.min(this.currentPage * this.pageSize, count);

            return this.strings.pageRange.replace(":from", from).replace(":to", to).replace(":count", count);
        },

        // Each button is aria-disabled at its end, never disabled, so the press that reaches the last page
        // keeps the focus it holds until focusPageStart() moves it on; a press at the end does nothing.
        showPreviousPage() {
            if (!this.isFirstPage()) {
                this.goToPage(this.currentPage - 1);
            }
        },

        showNextPage() {
            if (!this.isLastPage()) {
                this.goToPage(this.currentPage + 1);
            }
        },

        // A page is drawn at once, never through $moveRows: its rows are not the ones that were on screen, so
        // nothing moves, and a view transition would morph one page's rows into another's. The range is said
        // in the polite region, and focus goes to the page's first row, which brings it on screen, where a
        // reader carries on reading.
        goToPage(page) {
            const target = Math.min(Math.max(1, page), this.pageCount());

            if (target === this.currentPage) {
                return;
            }

            this.currentPage = target;
            // A row's popups belong to a row that has left the screen.
            this.menuFor = null;
            this.aboveMenuFor = null;
            this.refresh();
            this.announce(this.pageRange());
            this.afterShow(() => this.focusPageStart());
        },

        // A sort, a filter or the page size changed: the list starts again from its first page. The change
        // says what it did itself.
        restartPages() {
            this.currentPage = 1;
        },

        // Rows per page (section 12 of the simplify plan): a pick-one in a disclosure, as Sort is. A new size
        // starts the list again from its first page and says its range; either way focus goes back to the
        // control, which now shows the size, and is brought on screen once the list above it has grown or
        // shrunk under it.
        pickPageSize(size) {
            if (!PAGE_SIZES.includes(size)) {
                return;
            }

            const isChanged = size !== this.pageSize;

            if (isChanged) {
                this.pageSize = size;
                this.restartPages();
                this.refresh();
                this.announce(this.pageRange());
            }

            this.closePanel(true);

            if (isChanged) {
                this.afterShow(() => this.rootElement.querySelector('[data-panel-trigger="page-size"]')?.scrollIntoView?.({ block: "nearest" }));
            }
        },

        // The page holding a row, which a link named or an answer moved, drawn at once; false when the
        // filters hide the row.
        showPageOf(id) {
            const index = this.matched.findIndex((row) => row.id === id);

            if (index === -1) {
                return false;
            }

            this.currentPage = this.pageOfEachRow()[index];
            this.refresh();

            return true;
        },

        isOnPage(id) {
            return this.visibleRows.some((row) => row.id === id);
        },

        // The first row's header, which names the row; a folded row's Undo, or its Close; else the count
        // above the list, so focus never falls to the page.
        focusPageStart() {
            const row = this.rowElements()[0];

            if (!row) {
                this.focusSummary();

                return;
            }

            const header = this.noticeOf(row.dataset.rowId) ? null : row.querySelector("[data-row-header]");

            if (header) {
                header.focus();
            } else {
                this.focusWithin(row, null);
            }
        },
    };
}
